import { FormattedInput } from "components/FormInputs/FormattedInput";
import { useField } from "formik";
import { ComponentPropsWithoutRef, useEffect, useMemo, useState } from "react";
import { cn, htmlToText } from "utils/helpers";
import { rfxSubmission } from "newui/features/Rfx/Submission";
import { TextLength } from "newui/features/Rfx/types/component-types";
import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Box,
} from "@chakra-ui/react";

type AdditionalCommentProps = {
	className?: string;
	isClientPreview?: boolean;
	isReadOnly?: boolean;
	questionName: string;
	required: boolean;
	textLength?: TextLength;
} & Partial<ComponentPropsWithoutRef<typeof FormattedInput>>;

export const AdditionalComment = ({
	className,
	isClientPreview = false,
	isReadOnly = false,
	textLength = TextLength.SHORT,
	questionName,
	...props
}: AdditionalCommentProps) => {
	const fieldName = `${questionName}-comment`;
	const [, { error, value }] = useField(fieldName);
	const characterLimit = rfxSubmission.helpers.getTextLengthValue(textLength);
	const [index, setIndex] = useState<number[]>([]);

	const subTitle = useMemo(() => {
		return `Maximum of ${characterLimit} characters.`;
	}, [characterLimit]);

	const hasValue = value && htmlToText(value).length > 0;

	useEffect(() => {
		if (hasValue) {
			setIndex([0]);
		} else {
			setIndex([]);
		}
	}, [hasValue]);

	const content = (
		// @ts-ignore
		<FormattedInput
			{...props}
			name={fieldName}
			characterLimit={characterLimit}
			rfq
			className="my-4 w-full"
			placeholder="Enter your answer here..."
			subTitle={subTitle}
			disabled={isReadOnly}
			// hideError
		/>
	);

	if (isReadOnly && !isClientPreview) {
		return hasValue ? (
			<div>
				<span>Additional Comment:</span>
				{content}
			</div>
		) : null;
	}

	return (
		<Accordion
			allowToggle
			index={index}
			onChange={(newIndex) =>
				!hasValue && setIndex(newIndex instanceof Array ? newIndex : [newIndex])
			}
		>
			<AccordionItem className="!border-y-0">
				<AccordionButton
					className={
						hasValue
							? "!m-0 cursor-default !p-0 hover:!bg-white"
							: "w-fit rounded-md border border-gray-400 bg-white text-blue-700 hover:!bg-blue-100"
					}
					disabled={hasValue}
				>
					<Box
						as="span"
						flex="1"
						textAlign="left"
					>
						{hasValue ? (
							"Optional Comment:"
						) : (
							<>
								<h2>
									Add Comment
									<AccordionIcon />
								</h2>
							</>
						)}
					</Box>
				</AccordionButton>
				<AccordionPanel className="border-b-0 !p-0">{content}</AccordionPanel>
			</AccordionItem>
		</Accordion>
	);
};
